import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Truck, User, Printer } from "lucide-react";

const VEHICLE_TYPES = ["Mini Truck (Tata Ace)", "Pickup (7ft)", "LCV 14ft", "17ft Truck", "19ft Truck", "22ft Truck", "32ft SXL Container", "32ft MXL Container", "Trailer", "Bus", "Car"];

export default function FleetPage() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: v }, { data: d }] = await Promise.all([api.get("/vehicles"), api.get("/admin/users?role=driver")]);
      setVehicles(v.vehicles); setDrivers(d.users || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-[#FF5A00]">OWN FLEET</div>
            <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="fleet-page">Northline own vehicles.</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-vehicle-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-11 px-5"><Plus className="w-4 h-4 mr-1" /> Add vehicle</Button>
            </DialogTrigger>
            <AddVehicleDialog drivers={drivers} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>

        <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {vehicles.map(v => (
            <div key={v.id} data-testid={`fleet-card-${v.id}`} className="p-5 bg-[#141414] border border-[#2A2A2A]">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline text-zinc-500">Vehicle</div>
                  <div className="font-display font-black text-2xl mt-1 font-mono-ll">{v.vehicle_number}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${v.active ? "bg-emerald-900/40 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>{v.active ? "Active" : "Inactive"}</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm text-zinc-300"><Truck className="w-4 h-4 text-[#FF5A00]" /> {v.vehicle_type} · {v.capacity_kg} kg</div>
              <div className="mt-1 flex items-center gap-2 text-sm text-zinc-300"><User className="w-4 h-4 text-[#FF5A00]" /> Driver: {v.driver_name || "Unassigned"}</div>
              {v.notes && <div className="mt-3 text-xs text-zinc-500 border-t border-[#2A2A2A] pt-3">{v.notes}</div>}
            </div>
          ))}
          {vehicles.length === 0 && (
            <div className="col-span-full p-12 border border-dashed border-[#2A2A2A] text-center text-zinc-500">No vehicles in the fleet yet. Add one.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AddVehicleDialog({ drivers, onDone }) {
  const [f, setF] = useState({ vehicle_number: "", vehicle_type: "", capacity_kg: "", driver_id: "", notes: "", active: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/vehicles", { ...f, capacity_kg: Number(f.capacity_kg || 0), driver_id: f.driver_id || null });
      toast.success("Vehicle added"); onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="bg-[#0A0A0A] border-[#2A2A2A]">
      <DialogHeader><DialogTitle className="font-display font-black">Add vehicle to fleet</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="add-vehicle-form">
        <div><Label className="overline text-zinc-400">Vehicle number</Label>
          <Input required value={f.vehicle_number} onChange={(e) => setF({ ...f, vehicle_number: e.target.value })}
            data-testid="vehicle-number-input" placeholder="MH12AB1234"
            className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" /></div>
        <div><Label className="overline text-zinc-400">Vehicle type</Label>
          <Select value={f.vehicle_type} onValueChange={(v) => setF({ ...f, vehicle_type: v })}>
            <SelectTrigger data-testid="vehicle-type-select" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{VEHICLE_TYPES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="overline text-zinc-400">Capacity (kg)</Label>
          <Input type="number" value={f.capacity_kg} onChange={(e) => setF({ ...f, capacity_kg: e.target.value })}
            data-testid="vehicle-capacity-input"
            className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" /></div>
        <div><Label className="overline text-zinc-400">Assigned driver (optional)</Label>
          <Select value={f.driver_id} onValueChange={(v) => setF({ ...f, driver_id: v })}>
            <SelectTrigger data-testid="vehicle-driver-select" className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue placeholder="Pick driver" /></SelectTrigger>
            <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · {d.phone}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="overline text-zinc-400">Notes</Label>
          <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
            className="mt-2 h-11 bg-[#141414] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" /></div>
        <DialogFooter>
          <Button type="submit" disabled={busy} data-testid="submit-vehicle-btn"
            className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-11 px-6">
            {busy ? "Saving..." : "Add vehicle"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
